/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React, { useEffect, useRef } from 'react';
import { bool, func,  object, array } from 'prop-types';
import {
    ClickAwayListener, makeStyles, MenuItem, MenuList, Paper,
} from '@material-ui/core';

const useStyles = makeStyles(() => ({
    wrapSuggestion: {
        position: 'relative',
    },
    autoSuggestion: {
        position: 'absolute',
        backgroundColor: '#FFF',
        zIndex: 10,
        width: '100%',
        padding: '0px 3px',
        maxHeight: '150px',
        overflowY: 'auto',
        overflowX: 'hidden',
    },
    addressList: {
        fontSize: '12px',
        padding: '4px 8px',
        fontWeight: '600',
        borderBottom: 'solid 1px #ecdede',
        minHeight: 'auto',
    },
    menuListWrapper: {
        padding: '0px',
    },
    errorMsg: {},
    topContainer: {
        zIndex: 999,
        position: 'relative',
        '& $autoSuggestion': {
            minWidth: '300px',
            zIndex: 999,
            maxWidth: '400px',
            width: 'fit-content',
            '@media screen and (max-width: 1024px)': {
                minWidth: '100%',
                maxWidth: '100%',
            },
        },
    },
    row: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
    },
    left: {
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    right: {
        display: 'flex',
        alignItems: 'center',
        color: '#1565c0',
        fontWeight: 600,
        marginLeft: 12,
        whiteSpace: 'nowrap',
    },
    backRow: {
        color: '#1565c0',
        fontWeight: 600,
    },
}));

const LoqateSuggestionList = ({
    suggestions,
    handleAutoCompleteBlur,
    handlePickAnAddress,
    handleClickAway,
    showModel,
    gfAutoFillAddressVariant,
    previousSuggestions,
    goBackToPreviousList,
}) => {
    const classes = useStyles();
    const suggestionRef = useRef(null);
    const erroStyle = suggestions && suggestions.results && suggestions.count === 0 ? classes.errorMsg : '';
    useEffect(() => {
        if (showModel && suggestionRef.current) {
            suggestionRef.current.scrollTop = 0;
        }
    }, [suggestions, showModel]);
    const loqateContainerCountRegex = /\s-\s(\d+\sAddresses?)$/i;

    const getLoqateSuggestionDisplay = (address) => {
        const baseText = address.text || '';
        const description = address.description || '';

        if (!address.isContainer) {
            return {
                label: description ? `${baseText}, ${description}` : baseText,
                meta: '',
            };
        }

        const countMatch = description.match(loqateContainerCountRegex);
        if (countMatch) {
            const locationText = description.replace(loqateContainerCountRegex, '').trim();
            return {
                label: locationText ? `${baseText}, ${locationText}` : baseText,
                meta: countMatch[1],
            };
        }

        return {
            label: description ? `${baseText}, ${description}` : baseText,
            meta: '',
        };
    };
    const getSuggestions = () => {
        const results = suggestions?.results || [];

        return (
            <>
                {previousSuggestions?.length > 0 && (
                    <MenuItem
                        className={`${classes.addressList} ${classes.backRow}`}
                        onClick={goBackToPreviousList}
                    >
                        ← Go back to previous list
                    </MenuItem>
                )}

                {results.length > 0 ? (
                    results.map((address) => {
                        const display = getLoqateSuggestionDisplay(address);
                        return (
                            <MenuItem
                                tabIndex={0}
                                className={classes.addressList}
                                key={address.id || address.suggestion}
                                onClick={() => {
                                    handlePickAnAddress(address);
                                    if (!address.isContainer) {
                                        handleClickAway(false);
                                    }
                                }}
                            >
                                <div className={classes.row}>
                                    <span className={classes.left}>
                                        {display?.label}
                                    </span>

                                    {address.isContainer && (
                                        <span className={classes.right}>
                                            {display.meta}
                                            <span style={{ marginLeft: 6 }}>
                                                &gt;
                                            </span>
                                        </span>
                                    )}
                                </div>
                            </MenuItem>
                        );
                    })
                ) : (
                    <MenuItem
                        className={classes.addressList}
                        key="No_Match_Found"
                    >
                        No address match found
                    </MenuItem>
                )}
            </>
        );
    };

    if (!showModel) {
        return null;
    }

    return (
        <div
            className={`${classes.topBox} ${gfAutoFillAddressVariant ? classes.topContainer : {}}`}
            id="sugestion-list"
            onBlur={handleAutoCompleteBlur}
        >
            <ClickAwayListener onClickAway={handleClickAway}>
                <div>
                    <div className={classes.wrapSuggestion}>
                        <Paper className={`${classes.autoSuggestion} ${erroStyle}`} ref={suggestionRef}>
                            <MenuList className={classes.menuListWrapper}>
                                {getSuggestions()}
                            </MenuList>
                        </Paper>
                    </div>
                </div>
            </ClickAwayListener>
        </div>
    );
};
LoqateSuggestionList.propTypes = {
    handleClickAway: func,
    handleAutoCompleteBlur: func,
    handlePickAnAddress: func,
    suggestions: object,
    showModel: bool,
    gfAutoFillAddressVariant: bool,
    previousSuggestions: array,
    goBackToPreviousList: func,
};

LoqateSuggestionList.defaultProps = {
    handleClickAway: () => {},
    handleAutoCompleteBlur: () => {},
    handlePickAnAddress: () => {},
    suggestions: {
        results: [],
        count: 0,
    },
    showModel: false,
    gfAutoFillAddressVariant: false,
    previousSuggestions: [],
    goBackToPreviousList: () => {},
};

export default LoqateSuggestionList;
